import { useCallback, useMemo } from "react";

import { fetchProductCatalogData } from "../client/client";
import {
    useUbpContext,
    CollectionKey,
    CollectionPlan,
    CollectionsInputs,
    UbpStep
} from "../context/index";
import {
    calculateAllowances,
    calculateUsage,
    offeringAllowances,
    skus,
    Sku
} from "../model/index";

export const useInputValues = () => useUbpContext().inputs.values;
export const useInputSetters = () => useUbpContext().inputs.setters;

export const useStep = () => useUbpContext().step;
export const useSetStep = () => useUbpContext().setStep;

export const useHasCollectionSelected = () => {
    const { collections } = useInputValues();
    return Object.values(collections).some((c) => c.selected);
};

export const useSetCollectionSelected = () => {
    const { setCollections } = useInputSetters();
    return useCallback(
        (key: CollectionKey, selected: boolean) => {
            setCollections((prev) => ({
                ...prev,
                [key]: { ...prev[key], selected }
            }));
        },
        [setCollections]
    );
};

export const useSetCollectionPlan = () => {
    const { setCollections } = useInputSetters();
    return useCallback(
        (key: CollectionKey, plan: CollectionPlan) => {
            setCollections((prev) => ({
                ...prev,
                [key]: { ...prev[key], plan }
            }));
        },
        [setCollections]
    );
};

export const useUsageOutput = () => {
    const inputs = useInputValues();
    return useMemo(() => calculateUsage(inputs), [inputs]);
};

export const useAiCreditsExceeded = () => {
    const usage = useUsageOutput();
    const allowances = useAllowancesOutput();
    const step = useStep();
    if (step === UbpStep.collections) {
        return false;
    }

    return usage.aiCredits > allowances.aiCredits;
};

export const useAllowancesOutput = () => {
    const inputs = useInputValues();
    return useMemo(
        () =>
            calculateAllowances({
                allowanceConfigs: offeringAllowances,
                inputs: {
                    ...inputs,
                    offeringSkus: collectionsToSkus(inputs.collections)
                }
            }),
        [inputs]
    );
};

const COLLECTION_SKU_MAP: Record<CollectionKey, Record<CollectionPlan, Sku>> = {
    teamwork: {
        free: skus.teamworkCollectionFree,
        standard: skus.teamworkCollectionStandard,
        premium: skus.teamworkCollectionPremium,
        enterprise: skus.teamworkCollectionEnterprise
    },
    service: {
        free: skus.serviceCollectionFree,
        standard: skus.serviceCollectionStandard,
        premium: skus.serviceCollectionPremium,
        enterprise: skus.serviceCollectionEnterprise
    },
    strategy: {
        free: skus.teamworkCollectionFree,
        standard: skus.teamworkCollectionStandard,
        premium: skus.teamworkCollectionPremium,
        enterprise: skus.teamworkCollectionEnterprise
    },
    software: {
        free: skus.teamworkCollectionFree,
        standard: skus.teamworkCollectionStandard,
        premium: skus.teamworkCollectionPremium,
        enterprise: skus.teamworkCollectionEnterprise
    }
};

const collectionsToSkus = (collections: CollectionsInputs): Sku[] => {
    return (Object.keys(collections) as CollectionKey[])
        .filter((key) => collections[key].selected)
        .map((key) => COLLECTION_SKU_MAP[key][collections[key].plan]);
};

export const useLoadProductCatalogData = () => {
    const { setProductCatalog } = useUbpContext();

    return async function loadProductCatalogData() {
        setProductCatalog({ loading: true, data: null, error: null });
        try {
            const data = await fetchProductCatalogData(Object.values(skus));
            setProductCatalog({ loading: false, data, error: null });
        } catch (error: unknown) {
            setProductCatalog({ loading: false, data: null, error });
        }
    };
};

export const useIsProductCatalogDataReady = () => {
    const { productCatalog } = useUbpContext();
    return !productCatalog.loading && !!productCatalog.data;
};

export const useProductCatalogData = () => {
    return useUbpContext().productCatalog.data;
};
